import axiosInstance from "../services/AxiosInstance";
import { cachedRequest, clearCache } from "./requestCache";
import { resolveCourseId } from "../utils/courseId";

const API_URL = "/courses";

/** Server caps course list at 100 rows per page (see lms-api course.service). */
export const COURSE_LIST_PAGE_CAP = 100;
export const COURSE_LIST_DEFAULT_LIMIT = 10;

export const getcourses = async (limit = 10, page = 1, search = '', options = {}) => {
  const searchField = options.searchField ?? 'name';
  const safeLimit = Math.max(
    1,
    Math.min(COURSE_LIST_PAGE_CAP, Number.parseInt(limit, 10) || COURSE_LIST_DEFAULT_LIMIT)
  );
  const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
  const includeTotal = options.includeTotal !== false;
  return cachedRequest(
    `courses:list:${safeLimit}:${safePage}:${search}:${searchField}:${includeTotal ? '1' : '0'}`,
    60000,
    async () => {
    try {
      const response = await axiosInstance.get(API_URL, {
        params: {
          limit: safeLimit,
          page: safePage,
          search: search || '',
          searchField,
          includeTotal: includeTotal ? 'true' : 'false',
          ...(options.params || {}),
        },
      });
      const raw = response.data?.data ?? [];
      const data = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
      return {
        data,
        pagination: response.data?.pagination || {},
      };
    } catch (error) {
      console.error("❌ GET courseList error:", error.message, error.response?.data);
      throw error;
    }
  });
};

/** Load all courses by paging until the API returns a short/empty page. */
export const fetchAllCourses = async (search = '') => {
  const all = [];
  let page = 1;
  const maxPages = 500;
  while (page <= maxPages) {
    const res = await getcourses(COURSE_LIST_DEFAULT_LIMIT, page, search, { includeTotal: false });
    const chunk = Array.isArray(res?.data) ? res.data : [];
    if (!chunk.length) break;
    all.push(...chunk);
    if (chunk.length < COURSE_LIST_DEFAULT_LIMIT) break;
    page += 1;
  }
  return all;
};


// Create a new course
export const createcourses = async (courseData) => {
  try {
    const response = await axiosInstance.post(API_URL, courseData);
    clearCache("courses:");
    return response.data;
  } catch (error) {
    console.error("Error creating course", {
      error: error.response?.data,
      requestData: courseData,
    });
    throw error;
  }
};


// Update a course by ID
export const updatecourses = async (courseId, courseData) => {
  try {
    const response = await axiosInstance.put(`${API_URL}/${courseId}`, courseData);
    clearCache("courses:");
    return response.data;
  } catch (error) {
    console.error("Error updating course", error);
    throw error;
  }
};


// Delete a course by ID
export const deletecourses = async (courseId) => {
  try {
    const response = await axiosInstance.delete(`${API_URL}/${courseId}`);
    clearCache("courses:");
    return response.data;
  } catch (error) {
    console.error("Error deleting course", error);
    throw error;
  }
};


// Get a course by ID
export const getcoursesById = async (courseId) => {
  const id = resolveCourseId(courseId) || null;
  if (!id) {
    console.warn("getcoursesById: invalid courseId", courseId);
    throw new Error("Invalid course id");
  }
  return cachedRequest(`courses:byId:${id}`, 60000, async () => {
    try {
      const response = await axiosInstance.get(`${API_URL}/${id}`);
      return {
        data: response.data.data,
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error fetching course details", error);
      throw error;
    }
  });
};


/**
 * v2 billing app integrations (NEW)
 * Endpoints:
 *  - GET /departments
 *  - GET /branches
 */
// export const getDepartmentsV2 = async (limit = 10) => {
//   try {
//     const safeLimit = Math.max(1, Math.min(COURSE_LIST_PAGE_CAP, Number.parseInt(limit, 10) || COURSE_LIST_DEFAULT_LIMIT));
//     const res = await axiosInstance.get(`/departments?limit=${safeLimit}`);
//     return res.data?.data || res.data || [];
//   } catch (error) {
//     console.error("❌ GET departments (v2) error:", error.message, error.response?.data);
//     throw error;
//   }
// };


// export const getBranchesV2 = async (limit = 10) => {
//   try {
//     const safeLimit = Math.max(1, Math.min(COURSE_LIST_PAGE_CAP, Number.parseInt(limit, 10) || COURSE_LIST_DEFAULT_LIMIT));
//     const res = await axiosInstance.get(`/branches?limit=${safeLimit}`);
//     return res.data?.data || res.data || [];
//   } catch (error) {
//     console.error("❌ GET branches (v2) error:", error.message, error.response?.data);
//     throw error;
//   }
// };


// /**
//  * Get courses for dropdown
//  */
// export const getCoursesFromV1 = async (limit = 10) => {
//   try {
//     const safeLimit = Math.max(1, Math.min(COURSE_LIST_PAGE_CAP, Number.parseInt(limit, 10) || COURSE_LIST_DEFAULT_LIMIT));
//     const res = await axiosInstance.get(`/courses?limit=${safeLimit}`);
//     return res.data?.data || res.data || [];
//   } catch (error) {
//     console.error("❌ GET courses (v1) error:", error.message, error.response?.data);
//     throw error;
//   }
// };


export const getCoursesFromV1 = async (limit = 10, page = 1, search = '', options = {}) => {
  try {
    const safeLimit = Math.max(1, Math.min(COURSE_LIST_PAGE_CAP, Number.parseInt(limit, 10) || COURSE_LIST_DEFAULT_LIMIT));
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const safeSearch = search || '';
    const searchField = options.searchField ?? 'name';
    const includeTotal = options.includeTotal !== false;
    
    const response = await axiosInstance.get(API_URL, {
      params: {
        limit: safeLimit,
        page: safePage,
        search: safeSearch,
        searchField,
        includeTotal: includeTotal ? 'true' : 'false',
        ...(options.params || {}),
      },
    });
    
    // Return the full response structure with data and pagination
    return {
      data: response.data?.data || [],
      pagination: response.data?.pagination || {
        total: 0,
        totalPages: 0,
        currentPage: safePage,
        hasNext: false,
        hasPrev: false
      },
    };
  } catch (error) {
    console.error("❌ GET courses (v1) error:", error.message, error.response?.data);
    throw error;
  }
};

/**
 * Get departments with pagination
 */
export const getDepartmentsV2 = async (limit = 10, page = 1, search = '', options = {}) => {
  try {
    const safeLimit = Math.max(1, Math.min(COURSE_LIST_PAGE_CAP, Number.parseInt(limit, 10) || COURSE_LIST_DEFAULT_LIMIT));
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const safeSearch = search || '';
    const includeTotal = options.includeTotal !== false;
    
    const response = await axiosInstance.get('/departments', {
      params: {
        limit: safeLimit,
        page: safePage,
        search: safeSearch,
        includeTotal: includeTotal ? 'true' : 'false',
        ...(options.params || {}),
      },
    });
    
    // Return the full response structure with data and pagination
    return {
      data: response.data?.data || [],
      pagination: response.data?.pagination || {
        total: 0,
        totalPages: 0,
        currentPage: safePage,
        hasNext: false,
        hasPrev: false
      },
    };
  } catch (error) {
    console.error("❌ GET departments (v2) error:", error.message, error.response?.data);
    throw error;
  }
};

/**
 * Get branches with pagination
 */
export const getBranchesV2 = async (limit = 10, page = 1, search = '', options = {}) => {
  try {
    const safeLimit = Math.max(1, Math.min(COURSE_LIST_PAGE_CAP, Number.parseInt(limit, 10) || COURSE_LIST_DEFAULT_LIMIT));
    const safePage = Math.max(1, Number.parseInt(page, 10) || 1);
    const safeSearch = search || '';
    const includeTotal = options.includeTotal !== false;
    
    const response = await axiosInstance.get('/branches', {
      params: {
        limit: safeLimit,
        page: safePage,
        search: safeSearch,
        includeTotal: includeTotal ? 'true' : 'false',
        ...(options.params || {}),
      },
    });
    
    // Return the full response structure with data and pagination
    return {
      data: response.data?.data || [],
      pagination: response.data?.pagination || {
        total: 0,
        totalPages: 0,
        currentPage: safePage,
        hasNext: false,
        hasPrev: false
      },
    };
  } catch (error) {
    console.error("❌ GET branches (v2) error:", error.message, error.response?.data);
    throw error;
  }
};